import axios from 'axios';

/**
 * authFailedInterceptor — the ONE place the customer app reacts to
 * `SESSION_AUTH_REQUIRED` from the guest-session guard middleware.
 *
 * The guard runs on every `/api/ev/sessions/:sessionUid/*` request and
 * returns 401 `SESSION_AUTH_REQUIRED` when the recovery cookie is
 * missing, invalid, or issued for a different session. Once that
 * happens, EVERY subsequent guarded call in this browser tab will
 * fail the same way — polling forever is not useful. This interceptor
 * fires a single `onAuthFailed()` callback the first time it sees the
 * error and rejects the promise so callers still see the error.
 *
 * Explicitly NOT in scope:
 *   - Does not touch `localStorage`, `sessionStorage`, session UID,
 *     recovery cookie, or backend state. The failing session's UID +
 *     recovery metadata are preserved so support / OTP recovery can
 *     still find it.
 *   - Does not redesign auth, does not retry, does not attempt to
 *     re-mint the cookie.
 *   - Only matches URLs under `${apiBase}/ev/sessions/`. Every other
 *     axios call (auth, stations discovery, OCPP control) is
 *     ignored.
 */

// Matches the exact `/api/ev/sessions/:sessionUid` resource AND any
// descendant path (`/receipt/inferred`, `/start`, `/live`, `/summary`,
// `/payment/*`, …). Does NOT match `/api/ev/sessions` (creation) or
// unrelated routes like `/api/user/sessions/…` — the mandatory `/ev/`
// segment before `sessions` and the required non-empty next segment
// after `sessions/` enforce both boundaries.
const SESSION_RESOURCE_RE = /\/ev\/sessions\/[^/?#\s]+/;

function isSessionAuthFailedResponse(response /*, apiBase */) {
  if (!response) return false;
  if (response.status !== 401) return false;
  const body = response.data;
  if (!body || typeof body !== 'object') return false;
  if (body.error !== 'SESSION_AUTH_REQUIRED') return false;

  const rawUrl = String(response.config?.url || '');
  if (!rawUrl) return false;

  // Extract the path portion so query strings, fragments, hostnames,
  // and any trailing-slash quirks in the caller's `baseURL` cannot
  // trick the substring match. `URL(..., placeholder)` treats a
  // relative URL as rooted against the placeholder, so we get a
  // clean `pathname` either way.
  let path;
  try {
    path = new URL(rawUrl, 'http://placeholder.invalid').pathname;
  } catch {
    path = rawUrl;
  }

  return SESSION_RESOURCE_RE.test(path);
}

/**
 * Install a single response interceptor on the shared axios instance.
 * Returns an `eject()` fn that removes it — callers MUST call it on
 * unmount so a hot-reloaded provider doesn't stack interceptors.
 *
 * `onAuthFailed` is invoked at most once per install, even if many
 * requests hit the guard in the same tick.
 */
export function installAuthFailedInterceptor({ apiBase, onAuthFailed }) {
  let fired = false;
  const id = axios.interceptors.response.use(
    (r) => r,
    (error) => {
      if (isSessionAuthFailedResponse(error?.response, apiBase)) {
        if (!fired) {
          fired = true;
          try { onAuthFailed({ correlationId: error.response.data?.correlationId }); }
          catch { /* swallow — the caller's setState is best-effort */ }
        }
      }
      return Promise.reject(error);
    },
  );
  return () => {
    axios.interceptors.response.eject(id);
  };
}

export { isSessionAuthFailedResponse };
